import { useState, useEffect } from "react";
import { Users, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";

interface Participant {
  id: string;
  user_id: string;
  joined_at: string;
  progress: number;
  profile: {
    name: string;
    avatar_url: string | null;
  } | null;
}

interface ChallengeParticipantsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  challengeId: string;
  challengeTitle: string;
}

export const ChallengeParticipantsDialog = ({
  open,
  onOpenChange,
  challengeId,
  challengeTitle,
}: ChallengeParticipantsDialogProps) => {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchParticipants();
    }
  }, [open, challengeId]);

  const fetchParticipants = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_seasonal_challenges')
        .select('id, user_id, joined_at, progress')
        .eq('challenge_id', challengeId)
        .order('joined_at', { ascending: false });

      if (error) throw error;

      // Fetch profile info for each participant
      const participantsWithProfiles = await Promise.all(
        (data || []).map(async (item) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('name, avatar_url')
            .eq('id', item.user_id)
            .single();

          return {
            id: item.id,
            user_id: item.user_id,
            joined_at: item.joined_at || '',
            progress: item.progress || 0,
            profile: profile || null
          };
        })
      );

      setParticipants(participantsWithProfiles);
    } catch (error) {
      console.error('Error fetching participants:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Participants
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{challengeTitle}</p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : participants.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No participants yet</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-3">
              {participants.map((participant) => (
                <div
                  key={participant.id}
                  className="flex items-center gap-3 p-3 bg-secondary rounded-lg"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={participant.profile?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${participant.user_id}`}
                      alt={participant.profile?.name || 'User'}
                    />
                    <AvatarFallback>
                      {(participant.profile?.name || 'U').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">
                      {participant.profile?.name || 'Anonymous'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Joined {formatDistanceToNow(new Date(participant.joined_at), { addSuffix: true })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-primary">
                      {participant.progress}%
                    </p>
                    <p className="text-xs text-muted-foreground">progress</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
